import { PageCanvas } from "@/components/page-canvas";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  locationCustomerPath,
  locationVehiclePath,
  locationVehiclesPath,
} from "@/routes";
import type { VehicleProfile, VehicleOwner } from "./types";
import { CatalogueCredit, type VehicleCatalogue } from "./catalogue-fields";
import VehicleFields, {
  vehicleTargets,
} from "@/components/forms/vehicle-fields";
import { FormErrors } from "@/components/forms/form-errors";

export default function VehicleForm({
  vehicle,
  owner,
  catalogue,
}: {
  vehicle: VehicleProfile | null;
  owner: VehicleOwner;
  catalogue: VehicleCatalogue;
}) {
  const { auth, errors } = usePage<{ errors: Record<string, string> }>().props;
  const { t } = useTranslation();
  if (!auth.location) return null;
  const locationId = auth.location.id;
  const title = t(vehicle ? "vehicles.edit" : "vehicles.add");
  return (
    <AppLayout
      breadcrumbs={[
        { title: owner.name, href: locationCustomerPath(locationId, owner.id) },
      ]}
    >
      <Head title={title} />
      <PageCanvas>
        <h1 className="text-page font-display">{title}</h1>
        <Card>
          <CardHeader>
            <CardTitle>{t("vehicles.details")}</CardTitle>
          </CardHeader>
          <CardContent>
            <FormErrors errors={errors || {}} targets={vehicleTargets} />
            <Form
              key={vehicle?.revision || "new"}
              action={
                vehicle
                  ? locationVehiclePath(locationId, vehicle.id)
                  : locationVehiclesPath(locationId)
              }
              method={vehicle ? "patch" : "post"}
              encType="multipart/form-data"
            >
              {({ processing }) => (
                <div className="space-y-4">
                  <input type="hidden" name="customer_id" value={owner.id} />
                  {vehicle && (
                    <input
                      type="hidden"
                      name="revision"
                      value={vehicle.revision}
                    />
                  )}
                  <VehicleFields
                    values={vehicle}
                    catalogue={catalogue}
                    errors={errors}
                  />
                  <div className="space-y-2">
                    <Label htmlFor="vehicle-files">
                      {t("vehicles.photos")}
                    </Label>
                    <Input
                      id="vehicle-files"
                      name="vehicle[files][]"
                      type="file"
                      multiple
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      aria-describedby="vehicle-photo-help"
                      aria-invalid={!!errors?.files}
                    />
                    <p
                      id="vehicle-photo-help"
                      className="text-muted-foreground text-sm"
                    >
                      {t("vehicles.photo_help")}
                    </p>
                    {vehicle?.photos.map((photo) => (
                      <div key={photo.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`remove-photo-${photo.id}`}
                          name="remove_photo_ids[]"
                          value={photo.id}
                        />
                        <Label htmlFor={`remove-photo-${photo.id}`}>
                          {t("vehicles.remove_photo", { name: photo.name })}
                        </Label>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button type="submit" disabled={processing}>
                      {t(vehicle ? "vehicles.save" : "vehicles.add")}
                    </Button>
                    <Button
                      variant="outline"
                      render={
                        <Link
                          href={
                            vehicle
                              ? locationVehiclePath(locationId, vehicle.id)
                              : locationCustomerPath(locationId, owner.id)
                          }
                        />
                      }
                    >
                      {t("customers.cancel")}
                    </Button>
                  </div>
                </div>
              )}
            </Form>
            <CatalogueCredit />
          </CardContent>
        </Card>
      </PageCanvas>
    </AppLayout>
  );
}
